import { Box, ButtonBase } from '@mui/material'
import {
  VolumeOffOutlined as AutoOffIcon,
  VolumeUpOutlined as AutoOnIcon,
} from '@mui/icons-material'
import { type RefObject } from 'react'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import {
  FONT_WEIGHT,
  ICON_SIZE,
  UI_FONT_SIZE,
} from '@/shared/styles/typography'

// nagi の顔 (仮置き)。肩口までの正方形 480px。顔を決めたら差し替える
const PORTRAIT = '/images/counsel/nagi.jpg'

export type SeatState = 'idle' | 'busy' | 'speaking'

interface Props {
  state: SeatState
  // 読み上げ中に声の強弱で動かす輪 (useSpeech が書く)
  ringRef: RefObject<HTMLSpanElement>
  isAutoSpeak: boolean
  onToggleAutoSpeak: () => void
}

// 22px の ghost の丸 (行の読み上げと同じ大きさ)。タッチのときだけ当たり判定を 44px にする
export const roundGhostSx = {
  position: 'relative',
  flex: 'none',
  width: 22,
  height: 22,
  borderRadius: `${RADIUS.full}px`,
  color: colorTokens.fg.muted,
  transition: 'background-color 120ms',
  '&:hover': { backgroundColor: colorTokens.overlay.hover },
  '&.Mui-focusVisible': { boxShadow: colorTokens.focus.ringNeutral },
  '& svg': { fontSize: ICON_SIZE.sm },
  '@media (pointer: coarse)': {
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: -11,
    },
  },
} as const

/**
 * 相手の席 (design-book 04-apps/kakeru.md がんの相談・executive-rag.md 相手の席)。オンライン会議の相手の
 * 映像に当たり、会話の行はその発言録。上端に貼り付き、会話の前も後も同じ大きさで居続ける。
 * 輪が見えるのは処理している間だけ: 返事・要点を作成中は 1.5 秒の明滅、読み上げ中は声の強弱。
 * 顔は仮置きの写真 (下の PORTRAIT)。名札は会話の話者と同じ `nagi`。
 */
export const CounselSeat = ({
  state,
  ringRef,
  isAutoSpeak,
  onToggleAutoSpeak,
}: Props) => (
  <Box
    sx={{
      position: 'sticky',
      top: 0,
      zIndex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
      py: { xs: 1, sm: 2 },
      pb: { sm: 1.5 },
      backgroundColor: colorTokens.bg.default,
    }}
  >
    <Box
      sx={{
        position: 'relative',
        flex: 'none',
        width: { xs: 80, sm: 120 },
        height: { xs: 80, sm: 120 },
      }}
    >
      <Box
        component="img"
        src={PORTRAIT}
        alt=""
        sx={{
          display: 'block',
          width: '100%',
          height: '100%',
          borderRadius: `${RADIUS.full}px`,
          objectFit: 'cover',
          backgroundColor: colorTokens.bg.muted,
        }}
      />
      <Box
        component="span"
        ref={ringRef}
        aria-hidden
        sx={{
          position: 'absolute',
          inset: -6,
          border: `3px solid ${
            state === 'speaking'
              ? colorTokens.state.successFg
              : colorTokens.fg.muted
          }`,
          borderRadius: `${RADIUS.full}px`,
          opacity: 0,
          pointerEvents: 'none',
          ...(state === 'busy' && {
            animation: 'counsel-seat-busy 1.5s infinite',
          }),
          '@keyframes counsel-seat-busy': {
            '0%, 100%': { opacity: 0.5 },
            '50%': { opacity: 0.2 },
          },
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            ...(state !== 'idle' && { opacity: '0.5 !important' }),
            transform: 'none !important',
          },
        }}
      />
    </Box>
    {/* 名札が席の中央に来るよう、入り切りと同じ幅の空きを左に持つ */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        pl: '26px',
      }}
    >
      <Box
        component="span"
        sx={{
          fontSize: UI_FONT_SIZE.label,
          fontWeight: FONT_WEIGHT.medium,
          lineHeight: 1.4,
          color: colorTokens.fg.default,
        }}
      >
        nagi
      </Box>
      {/* 自動で読み上げの入り切り。オンライン会議の相手のタイルのスピーカーの印 */}
      <ButtonBase
        aria-pressed={isAutoSpeak}
        aria-label="自動で読み上げ"
        title="自動で読み上げ"
        onClick={onToggleAutoSpeak}
        sx={{
          ...roundGhostSx,
          ...(isAutoSpeak && { color: colorTokens.fg.default }),
        }}
      >
        {isAutoSpeak ? <AutoOnIcon /> : <AutoOffIcon />}
      </ButtonBase>
    </Box>
  </Box>
)
